import React from 'react';
import { useFormContext } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { computeText } from '@/locales/utils';
import type { Territory } from '@/pages/Restaurant/OperationZones/OperationZones.type';
import { isTerritoryCompatibleWithBrand } from '@/pages/Restaurant/OperationZones/OperationZones.utils';
import {
    StepWrapper,
    TerritoryList,
    TerritoryRadioRow,
    TerritoryFlag,
    TerritoryInfoBlock,
    TerritoryLabel,
    TerritoryCodeLabel,
    TerritorySolution,
    NoSelectionMessage,
} from '../CreateRestaurantForm.style';

interface OperationZoneSelectStepProps {
    territories: Territory[];
    brandCurrency: string | null;
    hasOpZoneRestaurant: boolean;
}

const OperationZoneSelectStep: React.FC<OperationZoneSelectStepProps> = ({
    territories,
    brandCurrency,
    hasOpZoneRestaurant,
}) => {
    const { setValue, watch } = useFormContext();
    const intl = useIntl();
    const selectedCode = watch('operationZoneCode');

    const handleSelect = (code: string | null) => {
        setValue('operationZoneCode', code, { shouldValidate: true });
        if (code === null) {
            setValue('operationZoneProvider', null);
        }
    };

    const isTerritoryDisabled = (territory: Territory): boolean =>
        !isTerritoryCompatibleWithBrand(territory, brandCurrency, hasOpZoneRestaurant);

    return (
        <StepWrapper>
            <NoSelectionMessage>
                {computeText(intl, 'brandRestaurants.create.operationZone.noSelectionMessage')}
            </NoSelectionMessage>
            <TerritoryList>
                {territories.map((territory) => {
                    const disabled = isTerritoryDisabled(territory);
                    return (
                        <TerritoryRadioRow
                            key={territory.id}
                            isSelected={!disabled && selectedCode === territory.code}
                            htmlFor={disabled ? undefined : `zone-${territory.id}`}
                            data-disabled={disabled || undefined}
                            title={
                                disabled
                                    ? computeText(intl, 'brandRestaurants.create.operationZone.currencyMismatch')
                                    : undefined
                            }
                        >
                            <input
                                type="radio"
                                id={`zone-${territory.id}`}
                                name="operationZoneCode"
                                checked={selectedCode === territory.code}
                                disabled={disabled}
                                onChange={() => !disabled && handleSelect(territory.code)}
                            />
                            <TerritoryFlag>{territory.flag}</TerritoryFlag>
                            <TerritoryInfoBlock>
                                <TerritoryLabel>
                                    {territory.name} <TerritoryCodeLabel>({territory.code})</TerritoryCodeLabel>
                                </TerritoryLabel>
                                <TerritorySolution>
                                    {computeText(intl, 'operationZones.solution')}:{' '}
                                    {territory.solution === 'operationZones.multiProvider'
                                        ? computeText(intl, 'operationZones.multiProvider')
                                        : territory.solution}
                                </TerritorySolution>
                            </TerritoryInfoBlock>
                        </TerritoryRadioRow>
                    );
                })}
            </TerritoryList>
        </StepWrapper>
    );
};

export default OperationZoneSelectStep;
